import React, { useState, useEffect } from 'react';
import { Badge, IconButton, Tooltip, Box, keyframes } from '@mui/material';
import { Chat as ChatIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { blue } from '@mui/material/colors';
import chatService from '../../api/chatService';
import websocketService from '../../api/websocketService';

interface ChatNotificationBadgeProps {
  onClick?: () => void;
}

// Create pulse animation
const pulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(25, 118, 210, 0.7);
  }
  70% {
    box-shadow: 0 0 0 6px rgba(25, 118, 210, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(25, 118, 210, 0);
  }
`;

const ChatNotificationBadge: React.FC<ChatNotificationBadgeProps> = ({ onClick }) => {
  const [unreadCount, setUnreadCount] = useState(0);
  const [hasFetched, setHasFetched] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Fetch unread count on mount
    fetchUnreadCount();
    
    // Set up WebSocket handler for unread count
    const handleUnreadCount = (count: number) => {
      console.log('Unread count from WebSocket in navbar badge:', count);
      setUnreadCount(count);
    };
    
    // Register WebSocket event handler
    websocketService.onUnreadCount(handleUnreadCount);
    
    // Fallback polling for cases when WebSocket fails
    const intervalId = setInterval(() => {
      if (!websocketService.isConnected()) {
        console.log('WebSocket not connected in navbar, using polling fallback');
        fetchUnreadCount();
      }
    }, 30000);

    return () => {
      // Clean up WebSocket handler
      websocketService.offUnreadCount(handleUnreadCount);
      clearInterval(intervalId);
    };
  }, []);

  const fetchUnreadCount = async () => {
    try {
      const response = await chatService.getUnreadCount();
      if (response) {
        setUnreadCount(response.count);
        setHasFetched(true);
      }
    } catch (error) {
      console.error('Failed to fetch unread count:', error);
      setHasFetched(true);
    }
  };

  const handleClick = (e: React.MouseEvent) => {
    // Stop propagation to prevent parent components from handling the click
    e.preventDefault();
    e.stopPropagation();
    
    if (onClick) {
      onClick();
    } else {
      // Use navigate instead of relying on Link behavior
      navigate('/chat');
    }
  };

  if (!hasFetched) {
    return (
      <IconButton color="inherit" disabled sx={{ opacity: 0.7 }}>
        <ChatIcon />
      </IconButton>
    );
  }

  return (
    <IconButton
      size="small"
      color="inherit"
      sx={{ 
        display: 'inline-flex',
        p: 0.5
      }}
      onClick={handleClick}
    >
      <Tooltip title={unreadCount > 0 ? `${unreadCount} unread message${unreadCount !== 1 ? 's' : ''}` : 'Messages'}>
        <Badge 
          badgeContent={unreadCount > 0 ? unreadCount : 0}
          color="error"
          invisible={unreadCount === 0} // Only show badge when there are unread messages
          sx={{
            '& .MuiBadge-badge': {
              backgroundColor: '#ef5350',
              fontSize: '0.7rem',
              minWidth: 16,
              height: 16,
              padding: '0 4px',
              fontWeight: 'bold'
            }
          }}
        >
          <ChatIcon 
            fontSize="small"
            sx={{
              color: 'inherit'
            }}
          />
        </Badge>
      </Tooltip>
    </IconButton>
  );
};

export default ChatNotificationBadge;